import { type AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { type SearchCondition, type DataGridRow } from "../types";
import { SendToTrackingService } from "@/api/handlers/errors";
import { LakersBackendApiRequestTimeoutError } from "@/utils/errors";
import { format } from "date-fns";

const TIMEOUT_MILLISECOND = 60_000;

export const getRightsSectionOtsukuListAPI = async (
  apiClient: AxiosInstance,
  searchCondition: SearchCondition
): Promise<DataGridRow[]> => {
  const params = new URLSearchParams();

  // 都道府県（複数）
  searchCondition.prefectures.forEach((id) => {
    params.append("prefectures", id.toString());
  });

  // 所在
  if (searchCondition.location != null && searchCondition.location !== "") {
    params.append("location", searchCondition.location);
  }

  // 債務者名
  if (searchCondition.debtorName != null && searchCondition.debtorName !== "") {
    params.append("debtor_name", searchCondition.debtorName);
  }

  // 抵当権者名
  if (
    searchCondition.mortgageeName != null &&
    searchCondition.mortgageeName !== ""
  ) {
    params.append("mortgagee_name", searchCondition.mortgageeName);
  }

  // 登記原因の区分（プルダウン選択時）
  if (searchCondition.reasonGroupId != null) {
    params.append("reason_group_id", searchCondition.reasonGroupId.toString());
  }

  // 登記原因（「その他」選択時の自由入力）
  if (searchCondition.reason != null && searchCondition.reason !== "") {
    params.append("reason", searchCondition.reason);
  }

  // 受付年月日（開始）
  if (searchCondition.acceptanceDateFrom != null) {
    params.append(
      "acceptance_date_from",
      format(searchCondition.acceptanceDateFrom, "yyyy-MM-dd")
    );
  }

  // 受付年月日（終了）
  if (searchCondition.acceptanceDateTo != null) {
    params.append(
      "acceptance_date_to",
      format(searchCondition.acceptanceDateTo, "yyyy-MM-dd")
    );
  }

  // 共同担保
  if (searchCondition.hasJointSecurity != null) {
    params.append(
      "has_joint_security",
      searchCondition.hasJointSecurity ? "true" : "false"
    );
  }

  // 現在事項
  if (searchCondition.isCurrentOnly != null) {
    params.append(
      "is_current_only",
      searchCondition.isCurrentOnly ? "true" : "false"
    );
  }

  // タイムアウトの実装
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, TIMEOUT_MILLISECOND);

  try {
    const response = await apiClient.get<DataGridRow[]>(
      "/picture-request/rights-section-otsuku?" + params.toString(),
      { signal: controller.signal }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.code === "ERR_CANCELED") {
      throw new LakersBackendApiRequestTimeoutError(
        "該当するデータが多すぎて処理できませんでした。より詳細な条件で再度検索してください。"
      );
    }

    if (
      isAxiosError(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      SendToTrackingService(error);
    }

    // その他のエラー時は空配列を返して検索結果なしと扱う
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
};
